import s from './Tagline.module.css'

/**
 * The header, in three inks and one line.
 *
 * The count is the size of London rather than of the selection, which is never printed.
 *
 * No pronoun, deliberately. "We" invents a curation department for a calendar one person
 * keeps, and it would be the first untrue thing on the page; "someone" reads as though the
 * writer were not the picker. The passive says the same and is the shortest of the three,
 * which matters when this has to hold on one line at 360px.
 */
export type TaglineProps =
  /** The front page. `count` is `LandingPayload.counts.events`. */
  | { kind: 'landing'; count: number }
  /** A room. `venue` is the venue's name, already decided by the caller. */
  | { kind: 'venue'; venue: string }

/**
 * Comma-grouped, as Python's `f"{n:,}"` was.
 *
 * The locale is pinned rather than left to the reader's, because the default would render
 * 7,261 as "7.261" for a German reader and as "7 261" for a French one. The Python produced
 * one string for everybody and this has to as well — the number is being read alongside
 * English prose about London.
 */
function figure(count: number): string {
  return count.toLocaleString('en-GB')
}

/**
 * The same sentence with no markup, for the meta description a link preview reads.
 *
 * Listings rather than gigs, and the difference is the claim. 7,261 gigs would say London
 * has that many nights out in it, which is a number nobody can stand behind — the feeds
 * double-count and this calendar has spent weeks merging them. 7,261 listings is what was
 * published, and what somebody read to make this page.
 */
export function taglineText(count: number): string {
  return `Curated for you from ${figure(count)} London listings, kept by hand.`
}

export function Tagline(props: TaglineProps) {
  return (
    <div className={s.say}>
      <h1 className={s.tag}>
        {props.kind === 'landing' ? (
          <>
            <span className={s.claim}>Curated for you</span>
            <span className={s.from}>
              from <span className={s.fig}>{figure(props.count)}</span> London listings, kept by hand.
            </span>
          </>
        ) : (
          <>
            {/* The Python wrote `&rsquo;` here; the character itself is the same thing in a
                UTF-8 file, and JSX has no entity to hide behind. */}
            <span className={s.claim}>What’s on at</span>
            <span className={s.from}>{props.venue}</span>
          </>
        )}
      </h1>
    </div>
  )
}
